type WebMcpTool = {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  execute: (input: Record<string, unknown>) => Promise<{ content: Array<{ type: string; text: string }> } | string>;
};

type ModelContext = {
  registerTool: (tool: WebMcpTool, options?: { signal?: AbortSignal }) => Promise<void>;
};

declare global {
  interface Navigator {
    modelContext?: ModelContext;
  }
}

function navigateHash(path: string) {
  window.location.hash = path.startsWith('#') ? path : `#${path}`;
}

export async function registerWebMcpTools(signal?: AbortSignal): Promise<void> {
  const modelContext = navigator.modelContext;
  if (!modelContext || typeof modelContext.registerTool !== 'function') {
    return;
  }

  const tools: WebMcpTool[] = [
    {
      name: 'open_getdeleto_demo',
      description: 'Open the Get Deleto writing demo so the user can start a continuous-writing session.',
      inputSchema: {
        type: 'object',
        properties: {},
        additionalProperties: false,
      },
      execute: async () => {
        navigateHash('/demo');
        return {
          content: [{ type: 'text', text: 'Opened Get Deleto demo at #/demo.' }],
        };
      },
    },
    {
      name: 'open_getdeleto_intro',
      description: 'Open the Get Deleto intro / landing page explaining how the app works.',
      inputSchema: {
        type: 'object',
        properties: {},
        additionalProperties: false,
      },
      execute: async () => {
        navigateHash('/intro');
        return {
          content: [{ type: 'text', text: 'Opened Get Deleto intro at #/intro.' }],
        };
      },
    },
    {
      name: 'describe_getdeleto',
      description: 'Return a short description of Get Deleto and how continuous-writing sessions work.',
      inputSchema: {
        type: 'object',
        properties: {
          includeUrls: {
            type: 'boolean',
            description: 'Whether to include key URLs in the response',
          },
        },
        additionalProperties: false,
      },
      execute: async (input) => {
        const includeUrls = Boolean(input.includeUrls);
        let text =
          'Get Deleto is a continuous-writing web app. During a session, stopping for about six seconds deletes your text. Choose a timer or word goal, then write without pausing.';
        if (includeUrls) {
          text +=
            ' Demo: https://www.getdeleto.com/#/demo — Auth for agents: https://www.getdeleto.com/auth.md';
        }
        return { content: [{ type: 'text', text }] };
      },
    },
  ];

  for (const tool of tools) {
    if (signal?.aborted) return;
    await modelContext.registerTool(tool, signal ? { signal } : undefined);
  }
}
